import { useCallback } from 'react';
import type { CheckoutSessionPayload } from '../types/checkout';
import type { RecurrenceOutcome, RecurrenceResult } from '../types/recharge';
import { useCheckoutStore } from '../store/checkout';
import { useRechargeStore } from '../store/recharge';
import { useSessionStore } from '../store/session';
import { useCustomerStore } from '../store/customer';
import { useChannelStore } from '../store/channel';
import { useUIStore } from '../store/ui';
import checkoutService from '../services/checkoutService';
import eventService from '../services/eventService';
import { sendPostMessageCaptivePortal } from '../services/timPostMessages';
import { handleError } from '../lib/errors/handleError';
import {
  normalizeCheckoutResult,
  getCheckoutPaymentMethod,
  toPaymentType,
} from '../utils/formatters';
import { calcCheckoutExtraBenefits } from '../utils/planBenefits';
import { isRecurrenceOptinAccepted } from '../utils/recurrence';
import {
  getActivePromoForFace,
  getServerNow,
} from '../lib/promoCampaign/activePromo';
import { mountLoopEventData } from '../lib/tracking/utils';
import { MEU_TIM } from '../constants/channels';

const recurrenceResultOf = (
  outcome: RecurrenceOutcome,
  status: string | null = null,
  renewalDate: string | null = null,
): RecurrenceResult => ({ outcome, status, renewalDate });

// Só 'activated' e 'pending' são conhecidos hoje. Qualquer outro status cai em
// 'error' de propósito: tratar como sucesso o que não sabemos ser sucesso é o
// erro caro — diria ao cliente que a programada está ativa sem ela estar.
const RECURRENCE_OUTCOME_BY_STATUS: Record<string, RecurrenceOutcome> = {
  activated: 'success',
  pending: 'processing',
};

const mapRecurrenceResponse = (data: {
  status?: string;
  renewalDate?: string | null;
}): RecurrenceResult =>
  recurrenceResultOf(
    RECURRENCE_OUTCOME_BY_STATUS[data?.status ?? ''] ?? 'error',
    data?.status ?? null,
    data?.renewalDate ?? null,
  );

const mapRecurrenceFailure = (httpStatus?: number): RecurrenceResult => {
  // O 504 do BFF e um timeout/queda de rede do próprio axios (sem `response`,
  // logo sem status) são a mesma incerteza: a adesão pode ter sido criada e a
  // gente não conseguiu ler. Afirmar "não foi possível ativar" aí seria mentir
  // sobre uma programada que provavelmente existe. É o mesmo critério que o
  // syncRechargeResult já aplica na resultado-checkout.
  if (httpStatus === 504 || !httpStatus)
    return recurrenceResultOf('processing');

  // Qualquer outra falha — incluindo 4xx — é erro na programada, e a tela diz
  // isso. O cliente pediu a adesão neste mesmo pagamento (o aceite vem do
  // evento terminal), então não informar nada o deixaria achando que está
  // ativa.
  return recurrenceResultOf('error');
};

export const useCheckout = () => {
  const {
    checkoutSessionData,
    checkoutEventData,
    sdkSessionActive,
    iframeCheckoutActive,
    setCheckoutSessionData,
    setCheckoutEventData,
    setSdkSessionActive,
    setIframeCheckoutActive,
    isSdkCheckoutEnabled,
    setIsSdkCheckoutEnabled,
    recurrenceOptinStatus,
    setRecurrenceOptinStatus,
    recurrenceSessionData,
    setRecurrenceSessionData,
  } = useCheckoutStore();
  const {
    selectedRechargeValue,
    rechargeValueCampaign,
    targetMsisdn,
    setRechargeResult,
    setRecurrenceResult,
  } = useRechargeStore();
  const { msisdn, sessionLoopId } = useSessionStore();
  const { channel } = useChannelStore();
  const customer = useCustomerStore(state => state.customer);
  const planBenefits = customer?.carrierInfo?.planBenefits ?? null;
  const featureToggles = useCustomerStore(state => state.featureToggles);
  const { showError } = useUIStore();

  const isCheckoutActive = !!featureToggles?.smartCheckout?.checkout;
  const isSdkActive = !!featureToggles?.smartCheckout?.sdkCheckout;
  const paymentType = toPaymentType(
    getCheckoutPaymentMethod(checkoutEventData),
  );
  const sdkCheckoutEnv = featureToggles?.smartCheckout?.sdkEnv || 'production';

  // O aceite da programada chega por DUAS fontes, e qual delas existe depende
  // do caminho de integração. Os eventos do iframe são um subconjunto dos do
  // SDK, e o evento a mais do SDK é justamente o que nos impede de ver o
  // terminal:
  //
  // - iframe (captive): recebe o evento terminal `ok`, que traz
  //   `recurrence_payment_option` — booleano, `true` sempre que o cliente
  //   pediu a ativação (checkbox ou modo automático), `false` quando não
  //   pediu. Chega como boolean ou como a string 'true' conforme a origem.
  // - SDK (MeuTIM): nunca recebe o `ok`. Antes dele o SDK emite um evento
  //   `payment_state` que já vem com `status: CONFIRMED` — é ele que encerra
  //   o fluxo e derruba o listener, e ele NÃO carrega o aceite. Lá a única
  //   fonte é o `recurrence_status` ('on'/'off'/'auto') do evento de clique
  //   `payment_request`, que chega antes de qualquer coisa.
  //
  // Quando o terminal existe ele manda, porque é o sinal mais tardio: o
  // cliente pode ter revisado o aceite depois de clicar. O clique só entra
  // quando o terminal não chegou. Ver `docs/flow/v2/v2-fluxo-checkout.md`,
  // seção sobre o evento terminal nunca recebido no SDK.
  const terminalOptin = checkoutEventData?.recurrence_payment_option;
  const hasRecurrenceOptin =
    terminalOptin == null
      ? isRecurrenceOptinAccepted(recurrenceOptinStatus)
      : terminalOptin === true || terminalOptin === 'true';

  const createCheckoutSession = useCallback(
    async (isSDKLoaded: boolean) => {
      // Um checkout novo não herda nada da recarga anterior. Fica aqui, e não
      // na limpeza das páginas, porque este é o único ponto de entrada do
      // checkout (SDK e iframe passam por ele).
      //
      // O evento terminal é zerado junto: além de o aceite ser lido dele no
      // iframe, o `correlationId` que ele carrega é o que identifica a sessão
      // corrente — é com ele que `syncRecurrenceResult` descarta respostas
      // atrasadas.
      //
      // O aceite vindo do clique também é zerado: sem isso, uma segunda
      // recarga que não emita `payment_request` herdaria o aceite da primeira
      // e consultaria a programada de uma recarga que já passou.
      setCheckoutEventData(null);
      setRecurrenceOptinStatus(null);
      setRecurrenceResult(null);

      try {
        const products = [selectedRechargeValue];
        if (rechargeValueCampaign) products.push(rechargeValueCampaign);

        const payload: CheckoutSessionPayload = {
          msisdn,
          productIds: products.map(p => p.productId),
          recipient: targetMsisdn || msisdn,
          extraBenefits: calcCheckoutExtraBenefits(
            planBenefits,
            selectedRechargeValue.id,
            // Lido na hora da chamada: a sessão de checkout é criada no clique
            getActivePromoForFace(
              useCustomerStore.getState().featureToggles?.promoCampaign,
              planBenefits,
              selectedRechargeValue.id,
              getServerNow(useCustomerStore.getState().serverTimeOffsetMs),
            ),
          ),
          isSDKLoaded,
          ...(sessionLoopId ? { sessionLoopId } : {}),
        };

        const { data } = await checkoutService.getCheckoutUrl(msisdn, payload);
        setCheckoutSessionData(data);
        return data;
      } catch (err) {
        handleError(err, showError);
        return null;
      }
    },
    [
      msisdn,
      sessionLoopId,
      selectedRechargeValue,
      rechargeValueCampaign,
      targetMsisdn,
      planBenefits,
      setCheckoutSessionData,
      setCheckoutEventData,
      setRecurrenceOptinStatus,
      setRecurrenceResult,
      showError,
    ],
  );

  // Sessão de checkout para gerenciar a programada que o cliente já tem.
  //
  // Mora aqui, e não no useRecurrence, por dois motivos: é uma sessão de
  // SmartCheckout, mesma família do createCheckoutSession; e o useRecurrence é
  // o hook da programada antiga, que será apagado inteiro na limpeza — código
  // novo lá dentro transformaria o PR de deleção numa cirurgia.
  const createRecurrenceSession = useCallback(async () => {
    try {
      const { data } = await checkoutService.createRecurrenceSession(msisdn);
      setRecurrenceSessionData(data);
      return data;
    } catch (err) {
      handleError(err, showError);
      return null;
    }
  }, [msisdn, setRecurrenceSessionData, showError]);

  const syncRechargeResult = useCallback(
    async (correlationId: string) => {
      const { data } = await checkoutService.syncRecharge(
        msisdn,
        correlationId,
      );
      const { nsu, price, source } = data.data?.payment ?? {};
      const normalized = normalizeCheckoutResult(
        {
          nsu,
          date: Date.now(),
          selectedRechargeValue: price,
          source,
        },
        targetMsisdn || msisdn,
      );
      setRechargeResult({
        ...normalized,
        faceValue: selectedRechargeValue?.id,
        fromCheckout: true,
      });
      return normalized;
    },
    [msisdn, targetMsisdn, selectedRechargeValue, setRechargeResult],
  );

  // Consulta o desfecho da adesão à programada aceita dentro do checkout, pelo
  // mesmo correlationId da recarga. Nunca rejeita: a programada é informação
  // secundária e não pode alterar o desfecho da recarga, que é quem manda na
  // tela de resultado.
  const syncRecurrenceResult = useCallback(
    async (correlationId: string) => {
      let result: RecurrenceResult;

      try {
        const { data } = await checkoutService.syncRecurrence(
          msisdn,
          correlationId,
        );
        result = mapRecurrenceResponse(data);
      } catch (err) {
        const httpStatus = (err as { response?: { status?: number } })?.response
          ?.status;
        result = mapRecurrenceFailure(httpStatus);
      }

      // Uma consulta lenta pode chegar depois de o cliente ter desistido e
      // começado outra recarga. Gravar aí contaminaria a recarga nova com o
      // desfecho de outra: só grava se o correlationId consultado ainda for o
      // da sessão corrente.
      const currentCorrelationId =
        useCheckoutStore.getState().checkoutEventData?.correlationId;
      if (currentCorrelationId !== correlationId) return null;

      setRecurrenceResult(result);
      return result;
    },
    [msisdn, setRecurrenceResult],
  );

  // Calcula as 3 condições do SDK no momento da chamada (não no render),
  // salva na store como fonte de verdade e retorna o resultado.
  // Deve ser chamado apenas dentro do handleConfirm.
  const resolveSdkCheckout = useCallback(() => {
    const enabled =
      channel === MEU_TIM && isSdkActive && !!window.BemobiSDKLoaded;
    setIsSdkCheckoutEnabled(enabled);
    return enabled;
  }, [channel, isSdkActive, setIsSdkCheckoutEnabled]);

  const sendLoopEvent = useCallback(
    (type: string, extraTags: Record<string, unknown> = {}) => {
      const eventBody = mountLoopEventData({
        type,
        msisdn,
        sessionLoopId,
        channelName: channel,
        flattenedEventData: extraTags,
      });
      eventService.send(msisdn, eventBody).catch(() => {});
    },
    [msisdn, sessionLoopId, channel],
  );

  const sendRenderCheckoutEvent = useCallback(
    (isSDKLoaded: boolean) => {
      sendLoopEvent('renderCheckout', {
        feature_toggle: `${isSdkActive}`,
        isSDKLoaded: `${isSDKLoaded}`,
      });
    },
    [sendLoopEvent, isSdkActive],
  );

  const notifyCaptivePortal = useCallback(
    (type: string) => sendPostMessageCaptivePortal(type, channel),
    [channel],
  );

  return {
    checkoutSessionData,
    checkoutEventData,
    sdkSessionActive,
    iframeCheckoutActive,
    isCheckoutActive,
    isSdkCheckoutEnabled,
    resolveSdkCheckout,
    paymentType,
    sdkCheckoutEnv,
    hasRecurrenceOptin,
    setRecurrenceOptinStatus,
    setCheckoutEventData,
    setSdkSessionActive,
    setIframeCheckoutActive,
    createCheckoutSession,
    recurrenceSessionData,
    createRecurrenceSession,
    sendRenderCheckoutEvent,
    syncRechargeResult,
    syncRecurrenceResult,
    sendLoopEvent,
    notifyCaptivePortal,
  };
};
